import { Box, Button, Checkbox, IconButton, Modal, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Toolbar, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListItemBackground, PageHeader, TableHeader } from '../../../app/AppStyles';
import { DisplayName, GetDate, GetTime, SearchKeyFilter } from '../../../app/AppUtils';
import ConfirmationModal from '../../subcomponents/ConfirmationModal';
import ContentLoader from '../../subcomponents/ContentLoader';
import SearchField from '../../subcomponents/SearchField';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import SubscriptionAddCard from './SubscriptionAddCard';
import { deleteSubscriptions, putSubscriptions } from './subscriptionSlice';
import SubscriptionsMoveCard from './SubscriptionsMoveCard';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';

export default function SubscriptionsDisplay({ performanceId, activityId, partialPaymentPosible }) {
    const dispatch = useDispatch();

    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { aspNetUser } = useSelector((state) => state.settings.app);
    const { subscriptions } = useSelector((state) => state.subscription);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { user } = useSelector((state) => state.user);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [selected, setSelected] = useState([]);
    const [openNew, setOpenNew] = useState(false);
    const [openMove, setOpenMove] = useState(false);
    const [filter, setFilter] = useState("");

    const newClick = () => setOpenNew(!openNew);

    const callbackModalNew = () => {
        setOpenNew(false);
    };

    const callbackModalMove = () => {
        setSelected([]);
        setOpenMove(false);
    };

    const handleFilterResult = (item) => {
        return SearchKeyFilter(item, ["student.number", "student.firstName", "student.middleName", "student.lastName", "student.schoolclass"], filter);
    }

    function EnhancedTableHead({ onSelectAllClick, numSelected, rowCount }) {
        return (
            <TableHead>
                <TableRow>
                    {aspNetUser?.role?.includes("Subscriptions") &&
                        <TableCell padding="checkbox">
                            <Checkbox
                                color="primary"
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={onSelectAllClick}
                                inputProps={{ 'aria-label': 'select all' }}
                            />
                        </TableCell>
                    }
                    <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.STUDENT}{resourceFinal[appLanguage].words.NUMBER.toLowerCase()}</TableCell>
                    <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.NAME}</TableCell>
                    <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.CLASS}</TableCell>
                    <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.SUBSCRIPTION} {resourceFinal[appLanguage].words.CONFIRMED.toLowerCase()}</TableCell>
                    {(siomSettings?.enablePartialPayments && partialPaymentPosible) && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.PARTIAL_PAYMENT} {resourceFinal[appLanguage].words.STATUS.toLowerCase()}</TableCell>}
                    <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DATE_CREATED}</TableCell>
                </TableRow>
            </TableHead>
        );
    };

    function EnhancedTableToolbar({ numSelected }) {
        const deleteClick = () => {
            var unsubscriptions = [];
            selected.forEach(item => unsubscriptions.push({
                studentId: item.student.studentId,
                subscriptionId: item.id,
                performanceId: item.performance.id,
                createdBy: `AP/${user.number}`
            }));

            setSelected([]);
            dispatch(deleteSubscriptions(unsubscriptions));
        };

        const confirmClick = () => {
            var subscriptions = [];
            selected.forEach(item => subscriptions.push({
                id: item.id,
                isReservation: false,
                modifiedBy: `AP/${user.number}`,
            }));

            setSelected([]);
            dispatch(putSubscriptions(subscriptions));
        };

        return (
            <Toolbar sx={{
                display: 'flex',
                justifyContent: 'space-between',
                ...(numSelected > 0 && { bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity) })
            }}>
                {numSelected > 0 ? (
                    <>
                        <Typography
                            sx={{ flex: '1 1 100%' }}
                            color="inherit"
                            variant="subtitle1"
                            component="div"
                        >
                            {numSelected} {resourceFinal[appLanguage].words.SELECTED.toLowerCase()}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography
                            sx={{ ...PageHeader }}
                            variant="h6"
                            id="tableTitle"
                            component="div"
                        >
                            {resourceFinal[appLanguage].words.SUBSCRIPTIONS}
                        </Typography>
                    </>
                )}
                <SearchField initialValue={filter} onDebouncedChange={setFilter} />
                {numSelected > 0 ? (
                    <>
                        <ConfirmationModal
                            iconButton
                            icon={<CheckIcon color="secondary" />}
                            iconTooltip={resourceFinal[appLanguage].words.CONFIRM}
                            message={`${selected.length > 1 ? resourceFinal[appLanguage].messages.CONFIRM_RESERVATIONS : resourceFinal[appLanguage].messages.CONFIRM_RESERVATION}`}
                            confirmAction={() => confirmClick(selected)}
                        />
                        <Tooltip title={resourceFinal[appLanguage].words.MOVE}>
                            <IconButton onClick={() => setOpenMove(true)}>
                                <DriveFileMoveIcon color="secondary" />
                            </IconButton>
                        </Tooltip>
                        <ConfirmationModal
                            iconButton
                            icon={<DeleteIcon color="secondary" />}
                            iconTooltip={resourceFinal[appLanguage].words.DELETE}
                            message={`${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_1} ${numSelected} ${numSelected > 1 ? resourceFinal[appLanguage].words.STUDENTS.toLowerCase() : resourceFinal[appLanguage].words.STUDENT.toLowerCase()} ${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_3}`}
                            confirmAction={() => deleteClick(selected)}
                        />
                    </>
                ) : (
                    <>
                        {aspNetUser?.role?.includes("Subscriptions") &&
                            <Button variant="outlined" onClick={() => newClick("Head")} startIcon={<AddIcon />}>{resourceFinal[appLanguage].words.ADD}</Button>
                        }
                    </>
                )}
            </Toolbar>
        );
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = subscriptions.data?.filter(handleFilterResult).map((n) => n);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        if (!aspNetUser?.role?.includes("Subscriptions")) return;

        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        };

        setSelected(newSelected);
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - subscriptions.data?.filter(handleFilterResult).length) : 0;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (id) => selected.findIndex(x => x.id === id) !== -1;

    return (
        <ContentLoader
            successCondition={subscriptions.status === "success"}
            errorCondition={subscriptions.status === "failed"}
            content={
                <>
                    <Box sx={{ p: 0.5, ...ListItemBackground }}>
                        <Box>
                            <EnhancedTableToolbar numSelected={selected.length} />
                        </Box>
                        <TableContainer>
                            <Table>
                                <EnhancedTableHead numSelected={selected.length} onSelectAllClick={handleSelectAllClick} rowCount={subscriptions.data?.filter(handleFilterResult)?.length} />
                                <TableBody>
                                    {(rowsPerPage > 0 ? subscriptions.data?.filter(handleFilterResult)?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : subscriptions.data?.filter(handleFilterResult))?.map((item, index) => {
                                        const isItemSelected = isSelected(item.id);
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow hover={aspNetUser?.role?.includes("Subscriptions")} onClick={aspNetUser?.role?.includes("Subscriptions") ? (event) => handleClick(event, item) : undefined} role="checkbox" aria-checked={isItemSelected} tabIndex={-1} key={item.id} selected={isItemSelected} sx={{ cursor: aspNetUser?.role?.includes("Planner") ? 'pointer' : 'auto' }}>
                                                {aspNetUser?.role?.includes("Subscriptions") &&
                                                    <TableCell padding="checkbox">
                                                        <Checkbox color="primary" checked={isItemSelected} inputProps={{ 'aria-labelledby': labelId, }} />
                                                    </TableCell>
                                                }
                                                <TableCell align="center" id={labelId}>{item.student.number}</TableCell>
                                                <TableCell align="left" id={labelId}>{DisplayName(item.student.firstName, item.student.middleName, item.student.lastName)}</TableCell>
                                                <TableCell align="center" id={labelId}>{item.student.schoolclass}</TableCell>
                                                <TableCell align="center">{item.isReservation ? resourceFinal[appLanguage].words.NO : resourceFinal[appLanguage].words.YES}</TableCell>
                                                {(siomSettings?.enablePartialPayments && partialPaymentPosible) && <TableCell align="center">{item.partialPaymentsCompleted}/{item.partialPaymentsTotal}</TableCell>}
                                                <TableCell align="center">{GetDate(item.dateCreated)} {GetTime(item.dateCreated)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {(rowsPerPage > 0 ? subscriptions.data?.filter(handleFilterResult)?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : subscriptions.data?.filter(handleFilterResult))?.length === 0 && <TableRow><TableCell colSpan={4}>{resourceFinal[appLanguage].messages.RESULT_EMPTY}</TableCell></TableRow>}
                                    {emptyRows > 0 && <TableRow style={{ height: 53 * emptyRows }}><TableCell colSpan={4} /></TableRow>}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            sx={{ border: 0 }}
                                            rowsPerPageOptions={[10, 25, 50,
                                                {
                                                    label: subscriptions.data?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                                    value: subscriptions.data?.length < 100 ? -1 : 100
                                                }
                                            ]}
                                            count={subscriptions.data ? subscriptions.data?.filter(handleFilterResult)?.length : 0}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            ActionsComponent={TablePaginationActions}
                                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                                        />
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Modal open={openNew} onClose={callbackModalNew}>
                        <>
                            <SubscriptionAddCard
                                handleClose={callbackModalNew}
                                performanceId={performanceId}
                            />
                        </>
                    </Modal>

                    <Modal open={openMove} onClose={callbackModalMove}>
                        <>
                            <SubscriptionsMoveCard
                                handleClose={callbackModalMove}
                                performanceId={performanceId}
                                activityId={activityId}
                                studentIds={selected.map(item => item.student.studentId)}
                            />
                        </>
                    </Modal>
                </>
            }
        />
    );
}
